import { api } from "@convex/_generated/api"; // Corrected import path
import { useMutation, useQuery } from "convex/react";
import {
	Check,
	Copy,
	ExternalLink,
	FileUp,
	Lock,
	QrCode,
	Settings,
	Trash2,
	User,
	Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import QRCodeModal from "@/components/_shared/QRCodeModal";
import StatusBadge from "@/components/_shared/StatusBadge";
import Toast from "@/components/_shared/Toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RoomSettingsModal } from "./RoomSettingsModal"; // Import settings modal

// Type for a displayed room item
interface RoomItem {
	raumId: string;
	uploadModus: "anonym" | "eingeloggt" | "besitzer";
	nurBesitzerLoeschen: boolean;
	hatPasswort?: boolean;
	aufrufe?: number;
}

export function FileShareForm() {
	const [, navigate] = useLocation();
	const [password, setPassword] = useState("");
	const [uploadModus, setUploadModus] = useState<
		"anonym" | "eingeloggt" | "besitzer"
	>("eingeloggt");
	const [nurBesitzerLoeschen, setNurBesitzerLoeschen] = useState(true);
	const [erfolgsMeldung, setErfolgsMeldung] = useState<string | null>(null);
	const [fehlerMeldung, setFehlerMeldung] = useState<string | null>(null);
	// const [nutzerHash, setNutzerHash] = useState<string>(""); // Entfernt
	const [lastCreatedRoom, setLastCreatedRoom] = useState<RoomItem | null>(null);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [qrLink, setQrLink] = useState<string>("");
	const [copied, setCopied] = useState<string | null>(null);
	const [settingsModalRoomId, setSettingsModalRoomId] = useState<string | null>(
		null,
	);
	const [editRoomPassword, setEditRoomPassword] = useState("");
	const [editUploadModus, setEditUploadModus] = useState<
		"anonym" | "eingeloggt" | "besitzer"
	>("eingeloggt");
	const [editRoomNurBesitzerLoeschen, setEditRoomNurBesitzerLoeschen] =
		useState(true);

	// Convex Hooks
	const createRoomMutation = useMutation(api.fileShare.raumErstellen);
	const raumLoeschenMutation = useMutation(api.fileShare.raumLoeschen);
	// Einstellungen werden im Modal gespeichert
	// nutzerHash Argument entfernt
	const nutzerRaeumeQuery = useQuery(api.fileShare.nutzerRaeumeLaden, {});

	// Map server data for display
	const nutzerRaeume: RoomItem[] = React.useMemo(() => {
		if (!nutzerRaeumeQuery) return [];
		return (
			nutzerRaeumeQuery as Array<{
				raumId: string;
				uploadModus: "anonym" | "eingeloggt" | "besitzer";
				nurBesitzerLoeschen: boolean;
				hatPasswort?: boolean;
				aufrufe?: number;
			}>
		).map((room) => ({
			raumId: room.raumId,
			uploadModus: room.uploadModus,
			nurBesitzerLoeschen: room.nurBesitzerLoeschen,
			hatPasswort: room.hatPasswort ?? false,
			aufrufe: room.aufrufe ?? 0,
		}));
	}, [nutzerRaeumeQuery]);

	// Load or create user hash - ENTFERNT
	// useEffect(() => {
	// const storedHash = localStorage.getItem("fileShare_nutzerHash");
	// if (storedHash) {
	// setNutzerHash(storedHash);
	// } else {
	// const newHash = uuidv4();
	// localStorage.setItem("fileShare_nutzerHash", newHash);
	// setNutzerHash(newHash);
	// }
	// }, []);

	// Auto-hide toasts
	useEffect(() => {
		if (erfolgsMeldung) {
			const timer = setTimeout(() => setErfolgsMeldung(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [erfolgsMeldung]);

	useEffect(() => {
		if (fehlerMeldung) {
			const timer = setTimeout(() => setFehlerMeldung(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [fehlerMeldung]);

	// Create a new FileShare room
	const handleCreateRoom = async () => {
		// nutzerHash-Prüfung entfernt

		try {
			// nutzerHash Argument entfernt
			const result = await createRoomMutation({
				passwort: password.trim() || undefined,
				uploadModus,
				nurBesitzerLoeschen,
			});

			setLastCreatedRoom({
				raumId: result.raumId,
				uploadModus,
				nurBesitzerLoeschen,
				hatPasswort: !!password.trim(),
			});

			setPassword("");
			setUploadModus("eingeloggt");
			setNurBesitzerLoeschen(true);
			setErfolgsMeldung("FileShare-Raum erfolgreich erstellt!");
		} catch (error) {
			setFehlerMeldung(
				"Fehler beim Erstellen des Raums. Bitte versuche es erneut.",
			);
			console.error("Error creating fileshare room:", error);
		}
	};

	// Raum löschen (nur in der Übersicht eigener Räume angezeigt)
	const handleDeleteRoom = async (raumId: string) => {
		if (!confirm("Diesen Raum und alle Dateien wirklich löschen?")) return;
		try {
			await raumLoeschenMutation({ raumId });
			setErfolgsMeldung("Raum gelöscht.");
		} catch (error) {
			setFehlerMeldung("Fehler beim Löschen des Raums.");
			console.error("Error deleting room:", error);
		}
	};

	// Copy room link to clipboard
	const handleCopyLink = (raumId: string) => {
		const url = `${window.location.origin}/file/${raumId}`;
		navigator.clipboard.writeText(url).then(
			() => {
				setCopied(url);
				setErfolgsMeldung("Link kopiert!");
				setTimeout(() => setCopied(null), 2000);
			},
			() => setFehlerMeldung("Kopieren fehlgeschlagen."),
		);
	};

	// Show QR code modal
	const handleShowQR = (raumId: string) => {
		setQrLink(`${window.location.origin}/file/${raumId}`);
		setQrModalOpen(true);
	};

	// Navigate to the room
	const handleGoToRoom = (raumId: string) => {
		navigate(`/file/${raumId}`);
	};

	// Open settings modal
	const handleEditSettings = (room: RoomItem) => {
		// const roomData = nutzerRaeumeQuery?.find((r) => r.raumId === room.raumId); // roomData wird nicht mehr für Passwort benötigt
		setEditRoomPassword(""); // Passwortfeld immer leer initialisieren für Sicherheit und Klarheit
		setEditUploadModus(room.uploadModus);
		setEditRoomNurBesitzerLoeschen(room.nurBesitzerLoeschen);
		setSettingsModalRoomId(room.raumId);
	};

	// Save settings from modal
	// Einstellungen werden direkt im Modal gespeichert

	// Render a single room item in the history list
	const renderRoomItem = (room: RoomItem) => (
		<div
			className="flex flex-col items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3 md:flex-row dark:border-gray-700 dark:bg-gray-800"
			key={room.raumId}
		>
			<div className="min-w-0 flex-1">
				<button
					className="group flex max-w-full cursor-pointer items-center gap-2 overflow-x-auto rounded-lg px-2 py-1 font-medium font-mono text-gray-700 text-lg transition-all hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
					onClick={() => handleCopyLink(room.raumId)}
					type="button"
				>
					{room.hatPasswort && (
						<Lock className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
					)}
					<span className="truncate">
						{window.location.origin}/file/
						<span className="text-blue-600 dark:text-blue-400">
							{room.raumId}
						</span>
					</span>
					{copied === `${window.location.origin}/file/${room.raumId}` ? (
						<Check className="h-5 w-5 shrink-0 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
					) : (
						<Copy className="h-5 w-5 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
					)}
				</button>
				<div className="mt-2 flex flex-wrap items-center gap-2 text-xs md:flex-nowrap">
					<StatusBadge
						color="blue"
						icon={room.uploadModus === "anonym" ? Users : User}
						text={`Upload: ${room.uploadModus === "anonym" ? "Anonym" : room.uploadModus === "eingeloggt" ? "Eingeloggt" : "Besitzer"}`}
					/>
					<StatusBadge
						color="blue"
						icon={room.nurBesitzerLoeschen ? User : Trash2}
						text={`Löschen: ${room.nurBesitzerLoeschen ? "Besitzer" : "Alle"}`}
					/>
					<StatusBadge
						color={room.hatPasswort ? "amber" : "gray"}
						icon={Lock}
						text={room.hatPasswort ? "Geschützt" : "Offen"}
					/>
					<StatusBadge
						color="blue" // Assuming FileUp is for views/downloads count
						icon={FileUp}
						text={`${room.aufrufe || 0}x Aufrufe`}
					/>
				</div>
			</div>
			<div className="flex items-center gap-2 md:flex-col">
				<div className="flex gap-1">
					<Button
						className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
						onClick={() => handleDeleteRoom(room.raumId)}
						size="icon"
						title="Raum löschen"
						type="button"
						variant="ghost"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
					<Button
						className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
						onClick={() => handleEditSettings(room)}
						size="icon"
						title="Einstellungen bearbeiten"
						type="button"
						variant="ghost"
					>
						<Settings className="h-4 w-4" />
					</Button>
					<Button
						className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
						onClick={() => handleShowQR(room.raumId)}
						size="icon"
						title="QR-Code anzeigen"
						type="button"
						variant="ghost"
					>
						<QrCode className="h-4 w-4" />
					</Button>
					<Button
						className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
						onClick={() => handleGoToRoom(room.raumId)}
						size="icon"
						title="Raum öffnen"
						type="button"
						variant="ghost"
					>
						<ExternalLink className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);

	return (
		<div className="flex flex-col gap-8">
			{/* Toasts */}
			<Toast
				message={erfolgsMeldung || ""}
				onClose={() => setErfolgsMeldung(null)}
				visible={!!erfolgsMeldung}
			/>
			<Toast
				message={fehlerMeldung || ""}
				onClose={() => setFehlerMeldung(null)}
				type="error"
				visible={!!fehlerMeldung}
			/>

			{/* Room Creation Form */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Neuen FileShare-Raum erstellen
				</h2>
				<div className="space-y-3">
					<div>
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="password-input"
						>
							Passwort (optional)
						</Label>
						<Input
							className="border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="password-input"
							onChange={(e) => setPassword(e.target.value)}
							placeholder="Leer lassen für keinen Passwortschutz"
							type="password"
							value={password}
						/>
					</div>

					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						<div className="space-y-2">
							<Label className="text-gray-600 dark:text-gray-300">
								Upload-Modus
							</Label>
							<Select
								value={uploadModus}
								onValueChange={(v) =>
									setUploadModus(v as "anonym" | "eingeloggt" | "besitzer")
								}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="Upload-Modus wählen" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="anonym">Anonym</SelectItem>
									<SelectItem value="eingeloggt">Eingeloggt</SelectItem>
									<SelectItem value="besitzer">Besitzer</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-2">
							<Label
								className="text-gray-600 dark:text-gray-300"
								htmlFor="owner-only-delete"
							>
								Wer darf löschen?
							</Label>
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2">
									<Switch
										id="owner-only-delete"
										checked={nurBesitzerLoeschen}
										onCheckedChange={setNurBesitzerLoeschen}
									/>
									<span className="text-gray-600 dark:text-gray-300">
										Nur Besitzer darf löschen
									</span>
								</div>
								<Button
									className="whitespace-nowrap"
									onClick={handleCreateRoom}
									type="button"
									variant="secondary"
								>
									Raum erstellen
								</Button>
							</div>
						</div>
					</div>
				</div>

				{/* Display last created room */}
				{lastCreatedRoom && (
					<div className="mt-6 border-gray-100 border-t pt-4 dark:border-gray-700">
						<h3 className="mb-4 font-medium text-gray-700 text-lg dark:text-gray-200">
							Dein erstellter FileShare-Raum
						</h3>
						{renderRoomItem(lastCreatedRoom)}
					</div>
				)}
			</section>

			{/* User's Room History */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Deine letzten FileShares
				</h2>
				{nutzerRaeume.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<FileUp className="mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
						<p className="text-gray-500 text-lg dark:text-gray-400">
							Deine FileShare-Räume erscheinen hier
						</p>
						<p className="mt-2 text-gray-400 text-sm dark:text-gray-500">
							Erstelle einen Raum, um ihn im Verlauf zu sehen
						</p>
					</div>
				) : (
					<div className="space-y-4">{nutzerRaeume.map(renderRoomItem)}</div>
				)}
			</section>

			{/* QR Code Modal */}
			<QRCodeModal
				isOpen={qrModalOpen}
				link={qrLink}
				onClose={() => setQrModalOpen(false)}
				title="QR-Code für FileShare-Raum"
			/>

			{/* Settings Modal */}
			{settingsModalRoomId && (
				<RoomSettingsModal
					initialSettings={{
						passwort: editRoomPassword,
						uploadModus: editUploadModus,
						nurBesitzerLoeschen: editRoomNurBesitzerLoeschen,
					}}
					isOpen={!!settingsModalRoomId}
					onClose={() => setSettingsModalRoomId(null)}
					// nutzerHash entfernt
					onError={setFehlerMeldung}
					onSuccess={(msg) => {
						setErfolgsMeldung(msg);
						setSettingsModalRoomId(null);
						// Trigger refetch? Convex should handle this, but maybe force it if needed
						// queryClient.invalidateQueries(['nutzerRaeumeLaden', nutzerHash]);
					}}
					raumId={settingsModalRoomId}
				/>
			)}
		</div>
	);
}
